import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X, User, LogOut } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const navItems = [
  { to: "/", label: "Home", end: true },
  { to: "/products", label: "Compounds" },
  { to: "/lookbook", label: "Research Library" },
  { to: "/about", label: "About" },
  { to: "/shipping", label: "Shipping" },
  { to: "/faq", label: "FAQ" },
];

export const Header = () => {
  const { count } = useCart();
  const { isAuthenticated, user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 bg-cream-100/85 backdrop-blur-lg border-b border-charcoal-100/40"
    >
      <div className="container-x flex items-center justify-between h-14 md:h-16">
        <Link
          to="/"
          data-testid="logo-home-link"
          className="flex items-baseline pl-1 pr-4 md:pr-6 group select-none"
          aria-label="LuviaRx home"
        >
          <span
            className="font-serif text-[#1C1618]"
            style={{
              fontWeight: 600,
              fontSize: "44px",
              letterSpacing: "0.20em",
              lineHeight: 1,
            }}
          >
            LUVIA
          </span>
          <span
            className="font-serif text-[#8B2252] lowercase ml-[5px]"
            style={{
              fontWeight: 600,
              fontSize: "28px",
              letterSpacing: "0.10em",
              lineHeight: 1,
            }}
          >
            rx
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive
                    ? "text-charcoal-600"
                    : "text-charcoal-400 hover:text-charcoal-600"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1 sm:gap-2">
          {isAuthenticated ? (
            <>
              {user?.is_admin && (
                <Link
                  to="/admin"
                  data-testid="header-admin-link"
                  className="hidden md:inline-flex items-center gap-1.5 text-sm font-medium text-white bg-[#8B2252] hover:bg-[#71193f] px-3 py-2 rounded-full transition-colors"
                >
                  Admin
                </Link>
              )}
              <Link
                to="/account"
                data-testid="header-account-link"
                className="hidden md:inline-flex items-center gap-1.5 text-sm font-medium text-charcoal-500 hover:text-charcoal-600 px-3 py-2 rounded-full hover:bg-sage-100 transition-colors"
              >
                <User className="w-4 h-4" strokeWidth={1.8} />
                My Account
              </Link>
              <button
                onClick={logout}
                data-testid="header-logout-button"
                className="hidden md:inline-flex items-center gap-1.5 text-sm font-medium text-charcoal-400 hover:text-charcoal-600 px-3 py-2 rounded-full hover:bg-sage-100 transition-colors"
                aria-label="Sign out"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.8} />
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="header-login-link"
                className="hidden md:inline-flex items-center text-sm font-medium text-charcoal-500 hover:text-charcoal-600 px-3 py-2 rounded-full hover:bg-sage-100 transition-colors"
              >
                Login
              </Link>
              <Link
                to="/register"
                data-testid="header-register-link"
                className="hidden md:inline-flex items-center text-sm font-medium text-white bg-[#8B2252] hover:bg-[#71193f] px-4 py-2 rounded-full transition-colors"
              >
                Register
              </Link>
            </>
          )}

          <Link
            to="/cart"
            data-testid="header-cart-link"
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-sage-100 transition-colors"
            aria-label="Open cart"
          >
            <ShoppingBag className="w-5 h-5 text-charcoal-600" strokeWidth={1.6} />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-sage-500 text-white text-[10px] font-semibold flex items-center justify-center px-1"
              >
                {count}
              </span>
            )}
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-sage-100 transition-colors"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t border-charcoal-100/40 bg-cream-100">
          <div className="container-x py-4 flex flex-col gap-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                className="py-3 text-charcoal-600 font-medium"
              >
                {n.label}
              </NavLink>
            ))}
            <div className="border-t border-charcoal-100/40 my-2" />
            {isAuthenticated ? (
              <>
                {user?.is_admin && (
                  <NavLink to="/admin" onClick={() => setOpen(false)} className="py-3 font-medium text-[#8B2252]">
                    Admin
                  </NavLink>
                )}
                <NavLink to="/account" onClick={() => setOpen(false)} className="py-3 text-charcoal-600 font-medium">
                  My Account ({user?.first_name})
                </NavLink>
                <button
                  onClick={() => { setOpen(false); logout(); }}
                  data-testid="mobile-logout-button"
                  className="py-3 text-left text-charcoal-400 font-medium"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <NavLink to="/login" onClick={() => setOpen(false)} className="py-3 text-charcoal-600 font-medium">
                  Login
                </NavLink>
                <NavLink to="/register" onClick={() => setOpen(false)} className="py-3 font-medium text-[#8B2252]">
                  Create account
                </NavLink>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
